import { useCallback, useEffect, useRef, useState } from 'react';

import type { ImageUploadSlot, RequestUploadSlots, UploadFiles } from '../types';

interface Args {
  isOpen: boolean;
  max: number;
  requestUploadSlots: RequestUploadSlots;
  uploadFiles: UploadFiles;
}

export interface PhotoPickerUploads {
  consumeSlots: (count: number) => ImageUploadSlot[];
  ensureSlots: (count: number) => Promise<ImageUploadSlot[]>;
  isUploading: boolean;
  pendingSlots: ImageUploadSlot[];
  presignError: boolean;
  resetErrors: () => void;
  upload: (files: File[]) => Promise<string[]>;
  uploadError: boolean;
}

export function usePhotoPickerUploads({
  isOpen,
  max,
  requestUploadSlots,
  uploadFiles,
}: Args): PhotoPickerUploads {
  const [pendingSlots, setPendingSlots] = useState<ImageUploadSlot[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [presignError, setPresignError] = useState(false);

  // Stable ref so the effect doesn't re-run when requestUploadSlots identity changes
  const requestUploadSlotsRef = useRef(requestUploadSlots);
  requestUploadSlotsRef.current = requestUploadSlots;

  // On each open, reset the pool and pre-fetch all slots for the option's max
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setPendingSlots([]);
    setPresignError(false);
    let cancelled = false;
    requestUploadSlotsRef.current(max).then(
      (slots) => {
        if (!cancelled) {
          setPendingSlots(slots);
        }
      },
      () => {
        if (!cancelled) {
          setPresignError(true);
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [isOpen, max]);

  const fetchSlots = useCallback(
    async (count: number): Promise<ImageUploadSlot[]> => {
      if (count <= 0) {
        return [];
      }
      try {
        const slots = await requestUploadSlots(count);
        setPresignError(false);
        return slots;
      } catch {
        setPresignError(true);
        return [];
      }
    },
    [requestUploadSlots],
  );

  const ensureSlots = useCallback(
    async (count: number) => {
      const capped = Math.min(count, max);
      const need = capped - pendingSlots.length;
      if (need <= 0) {
        return pendingSlots.slice(0, capped);
      }
      const extra = await fetchSlots(need);
      const next = [...pendingSlots, ...extra];
      setPendingSlots(next);
      return next.slice(0, capped);
    },
    [pendingSlots, max, fetchSlots],
  );

  const consumeSlots = useCallback(
    (count: number) => {
      const used = pendingSlots.slice(0, count);
      setPendingSlots((prev) => prev.slice(count));
      return used;
    },
    [pendingSlots],
  );

  const upload = useCallback(
    async (files: File[]): Promise<string[]> => {
      setIsUploading(true);
      setUploadError(false);
      try {
        const slots = await ensureSlots(files.length);
        if (slots.length < files.length) {
          setUploadError(true);
          return [];
        }
        // Consume used slots so each upload gets a unique presigned URL
        setPendingSlots((prev) => prev.slice(slots.length));
        const urls = await uploadFiles(files, slots);
        return urls;
      } catch {
        setUploadError(true);
        return [];
      } finally {
        setIsUploading(false);
      }
    },
    [ensureSlots, uploadFiles],
  );

  const resetErrors = useCallback(() => {
    setUploadError(false);
    setPresignError(false);
  }, []);

  return {
    pendingSlots,
    ensureSlots,
    consumeSlots,
    upload,
    isUploading,
    uploadError,
    presignError,
    resetErrors,
  };
}
